import moment from "moment-timezone";
import { ISession } from "@eagerdog/interfaces";

export const weeklyScheduleZone = (timezone?: string) =>
    timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

const clock = (date: Date, zone: string) => moment.tz(date, zone).format("HH:mm");

const instructorName = (session: ISession) => {
    const instructor = session.instructors?.[0];
    return `${(instructor?.first_name || "").trim().toLowerCase()}|${(instructor?.last_name || "").trim().toLowerCase()}`;
};

export const isWeeklySeries = (sessions: ISession[], zone: string) => {
    if (!sessions.length) {
        return false;
    }
    const ordered = [...sessions].sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime());
    const first = ordered[0];
    const startClock = clock(new Date(first.start_date), zone);
    const endClock = clock(new Date(first.end_date), zone);
    const name = instructorName(first);
    const capacity = first.regular_capacity;

    return ordered.every((session, index) => {
        if (clock(new Date(session.start_date), zone) !== startClock) return false;
        if (clock(new Date(session.end_date), zone) !== endClock) return false;
        if (instructorName(session) !== name) return false;
        if (session.regular_capacity !== capacity) return false;
        if ((session.waitlist_size || 0) !== (first.waitlist_size || 0)) return false;
        if (index === 0) return true;
        const previous = moment.tz(ordered[index - 1].start_date, zone);
        const current = moment.tz(session.start_date, zone);
        return current.isSame(previous.clone().add(1, "week"));
    });
};

export const registrationBlockMessage = (dates: Date[], zone: string) => {
    const labels = dates.map((date) => moment.tz(date, zone).format("MMM D"));
    const list = labels.length === 1
        ? labels[0]
        : `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
    const has = labels.length === 1 ? "has" : "have";
    const those = labels.length === 1 ? "that session" : "those sessions";
    return `${list} still ${has} registrations. Remove those registrations before deleting ${those}.`;
};

export const sessionEntryCount = (session: ISession) =>
    (session.regular_entries || 0) + (session.dropin_entries || 0) + (session.waitlist_entries || 0);

interface IWeeklyFields {
    startDate: Date;
    fromTime: Date;
    toTime: Date;
    weeks: number;
    firstName: string;
    lastName: string;
    capacity: number;
    waitlistSize: number;
    zone: string;
    preservedIds: string[];
    entriesById: Map<string, ISession>;
}

export const generateWeeklySessions = (fields: IWeeklyFields): ISession[] => {
    const day = moment(fields.startDate).format("YYYY-MM-DD");
    const from = moment(fields.fromTime).format("HH:mm");
    const to = moment(fields.toTime).format("HH:mm");
    const weeks = Math.min(52, Math.max(1, Math.floor(fields.weeks) || 1));
    const sessions: ISession[] = [];

    for (let index = 0; index < weeks; index++) {
        const start = moment.tz(`${day} ${from}`, "YYYY-MM-DD HH:mm", fields.zone).add(index, "weeks");
        let end = moment.tz(`${day} ${to}`, "YYYY-MM-DD HH:mm", fields.zone).add(index, "weeks");
        if (!end.isAfter(start)) {
            end = end.add(1, "day");
        }
        const id = fields.preservedIds[index] || "";
        const saved = id ? fields.entriesById.get(id) : undefined;
        sessions.push({
            _id: id,
            start_date: start.toDate(),
            end_date: end.toDate(),
            instructors: [{
                _id: "",
                first_name: fields.firstName,
                last_name: fields.lastName,
                contact_type: "Instructor",
                phone: "123",
                email: "email",
            }],
            regular_capacity: fields.capacity,
            dropin_capacity: fields.capacity,
            waitlist_size: fields.waitlistSize,
            regular_entries: saved?.regular_entries,
            dropin_entries: saved?.dropin_entries,
            waitlist_enabled: saved?.waitlist_enabled,
            fee: saved?.fee,
        });
    }

    return sessions;
};

export const weeklySummary = (sessions: ISession[], zone: string) => {
    if (!sessions.length) {
        return "";
    }
    const first = moment.tz(sessions[0].start_date, zone);
    const last = moment.tz(sessions[sessions.length - 1].start_date, zone);
    const end = moment.tz(sessions[0].end_date, zone);
    const count = `${sessions.length} session${sessions.length === 1 ? "" : "s"}`;
    return `${count} · ${first.format("ddd MMM D")} – ${last.format("ddd MMM D")} · ${first.format("h:mm A")}–${end.format("h:mm A")}`;
};

export const weekdayLabel = (startDate: Date) => `Every ${moment(startDate).format("dddd")}`;
